import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { TriggeredBy } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { motion } from 'motion/react';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { useTranslation } from '@frontend/i18n';
import { InboxAiStatus } from '@frontend/inbox-ai-status';
import { ThreadBodyHooks } from '@frontend/thread-body';
import { theme } from '@frontend/theme';
import { Button, Dot, SkeletonLoader, useAlert } from '@frontend/design-system';

const MotionButton = motion(Button);
const MotionDot = motion(Dot);

type Props = {
  threadId: string;
  groupId: string;
  locationPhone: string;
  /** True when thread is calculated but doesn't exist yet (new conversation) */
  isNew?: boolean;
};

export const AiAssistantButton = ({ threadId, groupId, locationPhone, isNew }: Props) => {
  const { t } = useTranslation('mini-chat');
  const alert = useAlert();

  const { getThreadQuery } = ThreadBodyHooks.useGetThreadData({
    threadId: threadId || '',
    locationId: groupId,
    isNew,
  });

  const thread = getThreadQuery?.data?.pages?.[0]?.thread as Thread;
  const aiStatus = thread?.aiStatus;

  // Compute AI status based on seeded flag and locationPhone
  const effectiveStatus = InboxAiStatus.Hooks.useEffectiveAIStatus({
    aiStatus,
    locationPhone,
    locationId: groupId,
  });

  const [botIsActive, setBotIsActive] = useState<boolean>(effectiveStatus.isActive);
  const [isInitializing, setIsInitializing] = useState(false);

  const setBotActiveMutation = SMSDataV4.Mutations.useSetBotActiveMutation({
    options: {
      onSuccess: () => {
        setBotIsActive(true);
        setIsInitializing(false);
      },
      onError: () => {
        setIsInitializing(false);
      },
    },
  });

  const setBotInactiveMutation = SMSDataV4.Mutations.useSetBotInactiveMutation({
    options: {
      onSuccess: (_data, req) => {
        if (req.triggeredBy !== TriggeredBy.TRIGGERED_BY_PHONE_CONFIG_CHANGE) {
          alert.success(
            t('AI Receptionist is no longer active in this thread. It will return automatically in 30 days.')
          );
        }
        setBotIsActive(false);
        setIsInitializing(false);
      },
      onError: () => {
        setIsInitializing(false);
      },
    },
  });

  const isLoading = isInitializing || setBotActiveMutation.isLoading || setBotInactiveMutation.isLoading;

  const handleToggle = useCallback(() => {
    if (isLoading) return;

    if (botIsActive) {
      setBotInactiveMutation.mutate({
        locationId: groupId,
        threadId,
        triggeredBy: TriggeredBy.TRIGGERED_BY_WEAVE_USER,
      });
    }
  }, [botIsActive, groupId, threadId, setBotInactiveMutation, isLoading]);

  // Sync botIsActive with effective status
  useEffect(() => {
    setBotIsActive(effectiveStatus.isActive);
  }, [effectiveStatus.isActive]);

  // Handle initialization flow for unseeded threads with AI-enabled phone
  useEffect(() => {
    if (effectiveStatus.needsInitialization && !isInitializing && !setBotActiveMutation.isLoading) {
      setIsInitializing(true);
      const timeoutId = setTimeout(() => {
        setIsInitializing(false);
      }, 2000);

      setBotActiveMutation.mutate(
        {
          locationId: groupId,
          threadId,
          triggeredBy: TriggeredBy.TRIGGERED_BY_PHONE_CONFIG_CHANGE,
        },
        {
          onSettled: () => {
            clearTimeout(timeoutId);
          },
        }
      );

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [effectiveStatus.needsInitialization, groupId, threadId]);

  // Handle reactivation flow for seeded threads deactivated by phone config change
  useEffect(() => {
    if (effectiveStatus.needsReactivation && !isInitializing && !setBotActiveMutation.isLoading) {
      setIsInitializing(true);
      const timeoutId = setTimeout(() => {
        setIsInitializing(false);
      }, 2000);

      setBotActiveMutation.mutate(
        {
          locationId: groupId,
          threadId,
          triggeredBy: TriggeredBy.TRIGGERED_BY_PHONE_CONFIG_CHANGE,
        },
        {
          onSettled: () => {
            clearTimeout(timeoutId);
          },
        }
      );

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [effectiveStatus.needsReactivation, groupId, threadId]);

  // Handle reset flow for seeded threads with non-AI phone
  useEffect(() => {
    if (effectiveStatus.needsReset && !isInitializing && !setBotInactiveMutation.isLoading) {
      setIsInitializing(true);
      const timeoutId = setTimeout(() => {
        setIsInitializing(false);
      }, 2000);

      setBotInactiveMutation.mutate(
        {
          locationId: groupId,
          threadId,
          triggeredBy: TriggeredBy.TRIGGERED_BY_PHONE_CONFIG_CHANGE,
        },
        {
          onSettled: () => {
            clearTimeout(timeoutId);
          },
        }
      );

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [effectiveStatus.needsReset, groupId, threadId]);

  // Don't show UI if shouldShowUI is false
  if (!effectiveStatus.shouldShowUI || !botIsActive) return null;

  return (
    <motion.div
      css={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <MotionButton
        css={[
          {
            padding: 0,
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          },
          aiAssistantStyles,
        ]}
        hoverLabel={isLoading ? t('Bot is initializing...') : t('AI Receptionist is active')}
        iconName='aiweave-assistant'
        size='small'
        variant='secondary'
        onClick={handleToggle}
        disabled={isLoading}
      >
        {isLoading && (
          <SkeletonLoader
            shape='rectangle'
            css={{
              position: 'absolute',
              inset: 0,
              borderRadius: theme.borderRadius.medium,
            }}
          />
        )}
      </MotionButton>
      <MotionDot
        color={isLoading ? 'neutral' : botIsActive ? 'success' : 'neutral'}
        css={{
          position: 'static',
          display: 'inline-flex',
          transform: `translateY(0.2em) translateX(-0.2em)`,
          opacity: isLoading ? 0.4 : 1,
        }}
      />
    </motion.div>
  );
};

export const aiAssistantStyles = css`
  border-radius: ${theme.borderRadius.medium};
  background: linear-gradient(
    -90deg,
    ${theme.colors.primary10},
    ${theme.colors.secondary.seaweed5},
    ${theme.colors.primary10},
    ${theme.colors.secondary.seaweed5}
  );
  background-size: 400% 400%;
  animation: gradient 5s ease infinite;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: linear-gradient(
      -90deg,
      ${theme.colors.primary10},
      ${theme.colors.secondary.seaweed5},
      ${theme.colors.primary10},
      ${theme.colors.secondary.seaweed5}
    );
    background-size: 400% 400%;
    animation: gradient 5s ease infinite;
  }

  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;
